import React, { useState, useEffect } from "react";
import MenuIcon from "@mui/icons-material/Menu";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { Data } from "../Config/Static";
import { Collapse, IconButton, Drawer } from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useNavigation } from "../context/NavigationContext";

const drawerWidth = 240;
const closedWidth = 75;

export default function Sidebar({ open, setOpen, isMobile }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isFormDirtyCondition, setIsFormDirtyCondition } = useNavigation();

  const [activeSubMenu, setActiveSubMenu] = useState(null);
  const [activeMenu, setActiveMenu] = useState(null);
  const [openCollapses, setOpenCollapses] = useState({});

  useEffect(() => {
    // Determine active menu based on current URL
    let currentActiveMenu = null;
    let currentActiveSubMenu = null;
    let currentOpenCollapses = {};

    Data.menuItems.forEach(item => {
      if (item.link === location.pathname) {
        currentActiveMenu = item.title;
      } else if (item.subMenu) {
        item.subMenu.forEach(subItem => {
          if (subItem.link === location.pathname) {
            currentActiveMenu = item.title; // Parent category is active
            currentActiveSubMenu = item.title; // Open the sub-drawer for desktop
            currentOpenCollapses[item.title] = true; // Open collapse for mobile
          }
        });
      }
    });

    setActiveMenu(currentActiveMenu);
    // Keep sub-menus closed by default, unless explicitly opened by user interaction
    if (!open) {
      setActiveSubMenu(null);
    }
    // Do not set openCollapses based on URL, keep them closed by default
    setOpenCollapses({});
  }, [location.pathname, open]);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
    setActiveSubMenu(null);
  };

  const handleClick = (link, menuTitle) => {
    if (
      isFormDirtyCondition &&
      !window.confirm(
        "You have unsaved changes. Are you sure you want to leave?"
      )
    ) {
      return;
    }
    if (link) {
      navigate(link);
    }
    setIsFormDirtyCondition(false);
    if (!isMobile) {
      setActiveSubMenu(null); 
      setOpen(false); 
    }
    if (menuTitle) {
      setActiveMenu(menuTitle);
    }
    if (isMobile) {
      setOpen(false); 
    }
    setOpenCollapses({});
  };

  const handleMainMenuClick = (item) => {
    if (isMobile) {
      if (item.subMenu) {
        setOpenCollapses((prev) => ({ ...prev, [item.title]: !prev[item.title] }));
      } else {
        handleClick(item.link, item.title);
      }
    } else {
      setActiveMenu(item.title);
      if (!open) {
        handleDrawerOpen();
        return;
      }
      if (item.link && !item.subMenu) {
        handleClick(item.link, item.title);
        return;
      }
      if (item.subMenu) {
        setActiveSubMenu(activeSubMenu === item.title ? null : item.title);
        setOpenCollapses({});
      }
    }
  };

  const handleSubMenuItemClick = (item, parentTitle) => {
    if (item.link && !item.submenu) {
      handleClick(item.link, parentTitle);
      return;
    }

    if (item.submenu) {
      setOpenCollapses((prev) => ({
        ...prev,
        [item.title]: !prev[item.title],
      }));
    }
  };

  const activeMenuItem = Data.menuItems.find(
    (item) => item.title === activeSubMenu
  );

  return (
    <Box sx={{ display: "flex" }}>
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={open}
        onClose={handleDrawerClose}
        sx={{
          width: open ? drawerWidth : closedWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: open ? drawerWidth : closedWidth,
            boxSizing: "border-box",
            backgroundColor: "#3D52A0",
            color: "white",
          },
        }}
      >
        <List
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            color: "white",
          }}
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "start",
              ml: 1.2,
              mt: 1.5
            }}
          >
            <IconButton
              sx={{ color: "white" }}
              onClick={() => {
                if (open) {
                  handleDrawerClose();
                } else {
                  handleDrawerOpen();
                }
              }}
            >
              <MenuIcon />
            </IconButton>
          </Box>
          <Box>
            {Data.menuItems.map((item) => {
              const isActive = location.pathname === item.link || (item.subMenu && item.subMenu.some(subItem => subItem.link === location.pathname));
              const isCollapseOpen = openCollapses[item.title];

              return (
                <React.Fragment key={item.title}>
                  <ListItem disablePadding sx={{ display: "block", marginTop: "20px" }}>
                    <ListItemButton
                      selected={isActive}
                      sx={{
                        minHeight: 48,
                        justifyContent: open ? "initial" : "center",
                        px: 2.5,
                        color: isActive ? "#ffffff" : "rgba(255, 255, 255, 0.4)",
                        borderRight: 1,
                        borderRight: isActive ? "#ffffff" : "rgba(255, 255, 255, 0.4)",
                        backgroundColor: isActive
                          ? "rgba(255,255,255,0.12)"
                          : "transparent",
                        "&:hover": {
                          backgroundColor: "rgba(255,255,255,0.08)",
                        },
                        borderRight: '3px solid ' + (isActive ? '#ffffff' : 'rgba(255, 255, 255, 0.0)'),
                      }}
                      onClick={() => handleMainMenuClick(item)}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: open ? 3 : "auto",
                          justifyContent: "center",
                          fontSize: "20px",
                          color: "inherit",
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.title}
                        sx={{ display: open ? "block" : "none", color: "inherit" }}
                      />
                      {item.subMenu && open && (
                        isMobile ? (
                          <ExpandMore
                            sx={{
                              transition: "transform 0.2s",
                              transform: isCollapseOpen ? "rotate(180deg)" : "rotate(0deg)",
                            }}
                          />
                        ) : (
                          <ChevronRightIcon
                            sx={{
                              transition: "transform 0.2s",
                              transform:
                                activeSubMenu === item.title
                                  ? "rotate(90deg)"
                                  : "rotate(0deg)",
                              color: "inherit",
                            }}
                          />
                        )
                      )}
                    </ListItemButton>
                  </ListItem>
                  {isMobile && item.subMenu && (
                    <Collapse in={isCollapseOpen} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {item.subMenu.map((subItem, subIndex) => {
                          const isSubItemActive = location.pathname === subItem.link;
                          return (
                            <ListItemButton
                              key={subIndex}
                              sx={{
                                pl: 4,
                                color: isSubItemActive ? "#ffffff" : "rgba(255, 255, 255, 0.7)",
                                backgroundColor: isSubItemActive
                                  ? "rgba(255,255,255,0.12)"
                                  : "transparent",
                                "&:hover": {
                                  backgroundColor: "rgba(255,255,255,0.08)",
                                },
                              }}
                              onClick={() => handleClick(subItem.link, item.title)}
                            >
                              <ListItemIcon sx={{color: "inherit", minWidth: '40px'}}>{subItem.icon}</ListItemIcon>
                              <ListItemText primary={subItem.title} primaryTypographyProps={{ fontSize: '0.9rem' }} />
                            </ListItemButton>
                          );
                        })}
                      </List>
                    </Collapse>
                  )}
                </React.Fragment>
              );
            })}
          </Box>
        </List>
      </Drawer>

      {!isMobile && activeMenuItem && (
        <Drawer
          variant="persistent"
          open={activeSubMenu !== null}
          onClose={() => setActiveSubMenu(null)}
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
              left: open ? drawerWidth : closedWidth,
              backgroundColor: "#3D52A0",
              color: "white",
            },
          }}
        >
          <List>
            <ListItem sx={{ height: "40px" }}>
              <h2>{activeMenuItem.title}</h2>
            </ListItem>
            {activeMenuItem.subMenu.map((item, ind) => (
              <React.Fragment key={`${item.title}-${ind}`}>
                <ListItem disablePadding sx={{ display: "block" }}>
                  <ListItemButton
                    onClick={() =>
                      handleSubMenuItemClick(item, activeMenuItem.title)
                    }
                    sx={{
                      minHeight: 48,
                      px: 2.5,
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <ListItemText primary={item.title} />
                    {item.submenu &&
                      (openCollapses[item.title] ? (
                        <ExpandLess />
                      ) : (
                        <ExpandMore />
                      ))}
                  </ListItemButton>
                </ListItem>

                <Collapse
                  in={openCollapses[item.title]}
                  timeout="auto"
                  unmountOnExit
                >
                  <List component="div" disablePadding>
                    {item.submenu &&
                      item.submenu.map((sub, subIndex) => (
                        <ListItemButton
                          key={`${sub.title}-${subIndex}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleClick(sub.link, activeMenuItem.title);
                          }}
                          sx={{ pl: 4 }}
                        >
                          <ListItemText primary={sub.title} />
                        </ListItemButton>
                      ))}
                  </List>
                </Collapse>
              </React.Fragment>
            ))}
          </List>
        </Drawer>
      )}
    </Box>
  );
}